"use client";

import doctorAnimation from "@/public/images/kiosk/Doctor.json";
import { datadogLogs } from "@datadog/browser-logs";
import Lottie from "lottie-react";
import React from "react";

interface ComponentErrorBoundaryProps {
  children: React.ReactNode;
  componentName: string;
  onRestart?: () => void;
  fallback?: React.ReactNode;
}

interface ComponentErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary for Kiosk Components
 *
 * Catches errors in individual components (like content validation failures)
 * and displays a fallback UI instead of crashing the entire app.
 *
 * This is especially useful for:
 * - Content schema validation errors
 * - Missing content keys
 * - Component runtime errors
 */
export class ComponentErrorBoundary extends React.Component<
  ComponentErrorBoundaryProps,
  ComponentErrorBoundaryState
> {
  constructor(props: ComponentErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ComponentErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    datadogLogs.logger.error(
      `[Kiosk ComponentErrorBoundary] Error in ${this.props.componentName}`,
      {
        error: {
          kind: error.name || "ComponentError",
          message: error.message,
          stack: error.stack,
        },
        errorInfo,
      },
    );

    console.error(
      `[ComponentErrorBoundary] Error in ${this.props.componentName}:`,
      error,
      errorInfo,
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="w-full bg-transparent flex flex-col items-center justify-between p-8 relative">
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4 max-w-3xl gap-8">
            <div className="w-[130px] h-[130px] animate-[fadeIn_0.6s_ease-in]">
              <Lottie
                animationData={doctorAnimation}
                loop={false}
                className="w-full h-full"
              />
            </div>
            <h1 className="kiosk-h1-refactor text-kioskPrimary">
              Please go to the front desk
            </h1>
            <p className="font-normal text-[18px] kiosk-body-lg !text-kioskPrimary !leading-7">
              Our team will be happy to assist you.
            </p>
            {this.props.onRestart && (
              <button
                type="button"
                onClick={this.props.onRestart}
                className="max-h-[60px] w-fit text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 text-white bg-kioskPrimary px-8 py-3"
              >
                Start Over
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
